import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  convertToMinutes,
  generateFullDaySlots12Hour,
} from "../../Utils/utils.js";

export function getCellHeight(interval) {
  if (interval === 5) return 9;
  if (interval === 15) return 27;
  if (interval === 30) return 54;
  if (interval === 60) return 108;
  return 27;
}

/**
 * Current-time red line position, auto-scroll-to-now, and booking wrapper scroll coordination.
 */
export function useBookingTableCurrentTime({
  selectedDate,
  tableInterval,
  timeSlots,
  isCalendarGridReady,
  selectedOutlet,
  bookingFilters,
  selectedRoomFilter,
  filteredRoomIds,
  filteredItemIds,
  newAppointmentSlotsLength,
  isRoomFilterActive,
  isSidebarVisible,
  isTableLoading,
  bookingWrapperRef,
  bookingTableRef,
}) {
  const [linePosition, setLinePosition] = useState(null);
  const [scrollTimeout, setScrollTimeout] = useState(null);
  const isUserScrollingRef = useRef(false);
  const initialAutoScrollDoneRef = useRef(false);
  const autoScrollTimersRef = useRef([]);
  /** True while we scroll via scrollIntoView so the wrapper scroll listener does not cancel auto-scroll retries. */
  const programmaticScrollRef = useRef(false);
  const programmaticScrollEndTimerRef = useRef(null);
  const currentTimeRowRef = useRef(null);

  const beginProgrammaticScroll = useCallback(() => {
    if (programmaticScrollEndTimerRef.current != null) {
      clearTimeout(programmaticScrollEndTimerRef.current);
      programmaticScrollEndTimerRef.current = null;
    }
    programmaticScrollRef.current = true;
  }, []);

  const endProgrammaticScrollSoon = useCallback(() => {
    programmaticScrollEndTimerRef.current = window.setTimeout(() => {
      programmaticScrollRef.current = false;
      programmaticScrollEndTimerRef.current = null;
    }, 550);
  }, []);

  const prevRoomFilterModeRef = useRef(isRoomFilterActive);

  const updateLinePosition = () => {
    if (!selectedDate || isUserScrollingRef.current) return;

    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    const currentTime24 = `${currentHour < 10 ? "0" : ""}${currentHour}:${
      currentMinutes < 10 ? "0" : ""
    }${currentMinutes}`;
    const fullDaySlots = generateFullDaySlots12Hour(tableInterval);
    let exactPosition = null;
    const rowHeight = getCellHeight(tableInterval);

    for (let i = 0; i < fullDaySlots.length - 1; i++) {
      const slotStart = fullDaySlots[i].time24;
      const slotEnd = fullDaySlots[i + 1].time24;

      if (currentTime24 >= slotStart && currentTime24 < slotEnd) {
        const slotStartMinutes = convertToMinutes(slotStart);
        const slotEndMinutes = convertToMinutes(slotEnd);
        const currentTotalMinutes = convertToMinutes(currentTime24);
        const totalMinutes = slotEndMinutes - slotStartMinutes;
        const passedMinutes = currentTotalMinutes - slotStartMinutes;
        exactPosition =
          i * rowHeight + (passedMinutes / totalMinutes) * rowHeight;
        break;
      }
    }

    setLinePosition((prev) => {
      if (exactPosition !== null && prev !== exactPosition) {
        return exactPosition;
      }
      return prev;
    });
  };

  const scrollToApproximateCurrentTime = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    const timeSlotsArray = timeSlots || [];
    let closestSlotIndex = -1;
    let minDifference = Infinity;

    timeSlotsArray.forEach((slot, index) => {
      if (slot.time24) {
        const [slotHour, slotMinute] = slot.time24.split(":").map(Number);
        const slotTotalMinutes = slotHour * 60 + slotMinute;
        const currentTotalMinutes = currentHour * 60 + currentMinutes;
        const difference = Math.abs(slotTotalMinutes - currentTotalMinutes);
        if (difference < minDifference) {
          minDifference = difference;
          closestSlotIndex = index;
        }
      }
    });

    if (closestSlotIndex >= 0) {
      const firstCell = bookingTableRef.current?.querySelector(
        `[data-row="${closestSlotIndex}"]`
      );
      if (firstCell) {
        beginProgrammaticScroll();
        firstCell.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        endProgrammaticScrollSoon();
        return true;
      }
    }
    return false;
  };

  const scrollToCurrentTime = () => {
    if (typeof window !== "undefined" && sessionStorage.getItem("skipCurrentTimeAutoScroll") === "1") {initialAutoScrollDoneRef.current = true; return;}
    if (initialAutoScrollDoneRef.current) return;
    if (isUserScrollingRef.current) return;

    const tryScroll = (attempt = 1) => {
      if (initialAutoScrollDoneRef.current || isUserScrollingRef.current) return;
      const currentRow = currentTimeRowRef.current;
      if (currentRow) {
        beginProgrammaticScroll();
        currentRow.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        endProgrammaticScrollSoon();
        initialAutoScrollDoneRef.current = true;
        autoScrollTimersRef.current.forEach((id) => clearTimeout(id));
        autoScrollTimersRef.current = [];
        return;
      }

      const maxAttempts = 14;
      if (attempt < maxAttempts) {
        const delayMs = Math.min(280, 60 + attempt * 35);
        const id = setTimeout(() => tryScroll(attempt + 1), delayMs);
        autoScrollTimersRef.current.push(id);
      } else if (!initialAutoScrollDoneRef.current && !isUserScrollingRef.current) {
        const scrolled = scrollToApproximateCurrentTime();
        if (scrolled) {
          initialAutoScrollDoneRef.current = true;
        }
      }
    };

    const id = setTimeout(() => tryScroll(), 50);
    autoScrollTimersRef.current.push(id);
  };

  const scrollToCurrentTimeRef = useRef(scrollToCurrentTime);
  scrollToCurrentTimeRef.current = scrollToCurrentTime;

  const handleScroll = () => {
    if (programmaticScrollRef.current) return;

    isUserScrollingRef.current = true;
    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }
    if (autoScrollTimersRef.current.length > 0) {
      autoScrollTimersRef.current.forEach((id) => clearTimeout(id));
      autoScrollTimersRef.current = [];
    }
    initialAutoScrollDoneRef.current = true;

    const newTimeout = setTimeout(() => {
      isUserScrollingRef.current = false;
    }, 100);

    setScrollTimeout(newTimeout);
  };

  useEffect(() => {
    updateLinePosition();

    const interval = setInterval(updateLinePosition, 60000);
    return () => {
      clearInterval(interval);
    };
  }, [selectedDate, tableInterval]);

  const timeSlotsGridKey =
    timeSlots?.length > 0
      ? `${timeSlots.length}:${timeSlots[0]?.time24 ?? ""}:${
          timeSlots[timeSlots.length - 1]?.time24 ?? ""
        }`
      : "";

  const filterTableAutoScrollFingerprint = useMemo(() => {
    const bf = bookingFilters || {};
    const sortNum = (arr) =>
      Array.isArray(arr)
        ? [...arr]
            .map((v) => Number(v))
            .filter((n) => !Number.isNaN(n))
            .sort((a, b) => a - b)
            .join(",")
        : "";
    const sortStr = (arr) =>
      Array.isArray(arr) ? [...arr].map(String).sort().join(",") : "";
    return [
      sortStr(bf.gender),
      sortStr(bf.status),
      sortNum(bf.staffIds),
      sortNum(bf.roomIds),
      sortStr(bf.roomItem),
      bf.isFilterApplied ? "1" : "0",
      String(selectedRoomFilter || ""),
      sortNum(filteredRoomIds),
      sortNum(filteredItemIds),
    ].join("|");
  }, [bookingFilters, selectedRoomFilter, filteredRoomIds, filteredItemIds]);

  const prevFilterTableAutoScrollFingerprintRef = useRef(undefined);

  const clearAutoScrollTimers = useCallback(() => {
    autoScrollTimersRef.current.forEach((id) => clearTimeout(id));
    autoScrollTimersRef.current = [];
    if (programmaticScrollEndTimerRef.current != null) {
      clearTimeout(programmaticScrollEndTimerRef.current);
      programmaticScrollEndTimerRef.current = null;
    }
  }, []);

  const scheduleScrollToCurrentTime = useCallback(() => {
    if (typeof window !== "undefined" && sessionStorage.getItem("skipCurrentTimeAutoScroll") === "1") {
      initialAutoScrollDoneRef.current = true;
      return;
    }
    const timerId = setTimeout(() => {
      scrollToCurrentTimeRef.current();
    }, 100);
    autoScrollTimersRef.current.push(timerId);
  }, []);

  useEffect(() => {
    initialAutoScrollDoneRef.current = false;
    clearAutoScrollTimers();

    if (!isCalendarGridReady) {
      return clearAutoScrollTimers;
    }

    scheduleScrollToCurrentTime();

    return clearAutoScrollTimers;
  }, [
    selectedDate,
    selectedOutlet,
    tableInterval,
    timeSlotsGridKey,
    isCalendarGridReady,
    clearAutoScrollTimers,
    scheduleScrollToCurrentTime,
  ]);

  useEffect(() => {
    const fp = filterTableAutoScrollFingerprint;
    const prev = prevFilterTableAutoScrollFingerprintRef.current;
    if (prev === undefined) {
      prevFilterTableAutoScrollFingerprintRef.current = fp;
      return;
    }
    if (prev === fp) return;

    prevFilterTableAutoScrollFingerprintRef.current = fp;

    if (newAppointmentSlotsLength !== 0) return;
    if (!isCalendarGridReady) return;
    if (!initialAutoScrollDoneRef.current) return;
    if (typeof window !== "undefined" && sessionStorage.getItem("skipCurrentTimeAutoScroll") === "1") {
      return;
    }

    clearAutoScrollTimers();

    initialAutoScrollDoneRef.current = false;
    scheduleScrollToCurrentTime();
  }, [
    filterTableAutoScrollFingerprint,
    newAppointmentSlotsLength,
    isCalendarGridReady,
    clearAutoScrollTimers,
    scheduleScrollToCurrentTime,
  ]);

  useEffect(() => {
    if (isTableLoading) return;

    const bookingWrapper = bookingWrapperRef.current;
    if (!bookingWrapper) return;

    bookingWrapper.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      bookingWrapper.removeEventListener("scroll", handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [isTableLoading]);

  useEffect(() => {
    const modeChanged = prevRoomFilterModeRef.current !== isRoomFilterActive;
    prevRoomFilterModeRef.current = isRoomFilterActive;

    if (!modeChanged) return;

    if (isSidebarVisible) return;

    const scrollTimeoutId = setTimeout(() => {
      if (typeof window !== "undefined" && sessionStorage.getItem("skipCurrentTimeAutoScroll") === "1") return;
      if (isUserScrollingRef.current || !isCalendarGridReady) return;

      const currentRow = currentTimeRowRef.current;
      if (currentRow) {
        beginProgrammaticScroll();
        currentRow.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        endProgrammaticScrollSoon();
      }
    }, 100);

    return () => {
      clearTimeout(scrollTimeoutId);
    };
  }, [
    isRoomFilterActive,
    isSidebarVisible,
    isCalendarGridReady,
    beginProgrammaticScroll,
    endProgrammaticScrollSoon,
  ]);

  return {
    linePosition,
    currentTimeRowRef,
  };
}
